import { messageType, resultStatus } from "_src/engine/ipc.js";
import {memoryType, bufferType, attributeId} from "_src/engine/constants";
import workerManager from "_src/engine/worker_manager.js";
import arrayTools from "_src/engine/utility/arrays";
import Octant from "_src/engine/octree/octree_combiner";
import intersectEvaluator from "_src/engine/intersect_evaluator";
import memoryProfiler from "_src/engine/memory_profiler";
import minmaxUtils from "_src/engine/utility/minmax";
import config from "_src/config";

import events from "_src/engine/events";
import { vec3 } from "_src/libs/gl-matrix";

import {DEFAULT_ATTRIBUTES, getTypeByID, getTypedArrayByID} from "_src/engine/utility/attributes";

import buildHalfEdgeData from "_src/engine/data_builders/half_edges.js";
import buildOctreeData from "_src/engine/data_builders/octree_builder.js";
import {
  invertVerticesNormals,
  buildNormals
} from "_src/engine/data_builders/normals_builder";
import {buildBordersMask} from "_src/engine/data_builders/border_builder";

import output from "_src/engine/output";

const _INTERSECTION = {
  point     : vec3.create(),
  distance  : Infinity,
  primitive : 0
};

const DEPENDENT_BUFFER_NAMES  = ["indices", "a_position"];

class Geometry {

  constructor({ layout = {}, bbox = [0,0,0,0,0,0]}) {

    this._layout = layout;

    this._hasMarkedVertices = false; // true if "active_selection_mask" attribute has non-zero elements
    this._hasSelectedVertices = false; // true if "selection_mask" attribute has non-zero elements

    this._tid = workerManager.INVALID_ID;

    this.isPointCloud = !("indices" in layout);

    this.bbox = bbox;

    this.octree = null;

  }

  getLayoutItem(attrName) {

    const attrBuffer = this._layout[attrName];

    return attrBuffer ? attrBuffer : null;

  }

  hasLayoutItem(attrName) {

    const attrBuffer = this._layout[attrName];

    return attrBuffer ? true : false;

  }

  setLayoutItem(attrName, attribute) {

    const itemBuffer = this.getItemBuffer(attrName);
    if (itemBuffer) {
      memoryProfiler.markMemoryReleased(itemBuffer.buffer);
    }

    this._layout[attrName] = attribute;
  }

  deleteLayoutItem(attrName) {
    delete this._layout[attrName];
  }

  getLayout() {
    return this._layout;
  }

  getItemBuffer(attrName) {

    const attrBuffer = this._layout[attrName];

    return attrBuffer ? attrBuffer.getBuffer() : null;

  }

  getVerticesCount() {

    const positions = this.getItemBuffer("a_position");

    return positions ? positions.length / 3 : 0;

  }

  getTrianglesCount() {

    const indices = this.getItemBuffer("indices");

    return indices && !this.isPointCloud ? indices.length / 3 : 0;

  }

  initSelectionBuffers() {

    const length = this.getVerticesCount();
    const maskBuffer = new Uint8Array(length).buffer;
    const maskItem = new GeometryBuffer({ buffer : maskBuffer, type : bufferType.UI8_1 });
    this.setLayoutItem("a_selection_mask", maskItem);

    this.initAuxiliarySelectionBuffers(length);

  }

  initAuxiliarySelectionBuffers(verticesNum) {

    const activeSelectionMaskBuffer = new Uint8Array(verticesNum).buffer;
    const activeSelectionMaskItem = new GeometryBuffer({ buffer : activeSelectionMaskBuffer, type : bufferType.UI8_1 });
    this.setLayoutItem("active_selection_mask", activeSelectionMaskItem);

  }

  removeSelectionBuffers() {
    delete this._layout["a_selection_mask"];
    delete this._layout["active_selection_mask"];
    if (this.isPointCloud) {
      this.deletePointCloudIndices();
    }
  }

  initEmptyBuffer(name, number, type) {
    const bstride = arrayTools.getNumElements(type) * arrayTools.getBytesPerElement(type);
    const rowData = new ArrayBuffer(number * bstride);
    const data = arrayTools.castBuffer(rowData, type);
    const item = new GeometryBuffer({ buffer : data.buffer, type : type });
    this.setLayoutItem(name, item);
  }

  repairSelectionBuffers() {

    const mask = this.getItemBuffer("a_selection_mask");

    if (mask) {
      const activeMask = this.getItemBuffer("active_selection_mask");
      if (!activeMask || activeMask.length != mask.length) {
        this.initAuxiliarySelectionBuffers(mask.length);
      }
    }
  }

  removeHalfEdges() {
    delete this._layout["edges2edges"];
    delete this._layout["verts2edges"];
    delete this._layout["border_mask"];
  }

  removeOctree() {
    this.octree = null;
  }

  buildOctree(useWorker) {

    if (this.isPointCloud) {
      this._initPointCloudIndices();
    }

    const positionItem = this.getLayoutItem("a_position");
    if (positionItem.isDetached()) {
      return Promise.reject("Can't build octree. Positions buffer is detached.");
    }
    const indicesItem = this.getLayoutItem("indices");

    this.octree = null;

    const positions = positionItem.getBuffer();
    const indices = indicesItem.getBuffer();

    if (useWorker) {

      const message = {
        indices : indices,
        positions : positions,
        type : messageType.BUILD_OCTREE,
        bbox : this.bbox,
        tid : workerManager.generateUniqueID(),
        isPointCloud : this.isPointCloud
      };

      const transfer = [message.indices.buffer, message.positions.buffer];

      positionItem.setIsDetached(true);
      indicesItem.setIsDetached(true);

      for (let i = 0; i < transfer.length; ++i) {
        memoryProfiler.markMemoryReleased(transfer[i]);
      }

      this._tid = message.tid;

      return workerManager.sendMessage(message, transfer, message.tid).then(({ data }) => {

        if (data.status === resultStatus.ALL_OK) {
          this._constructOctree(data, positionItem, indicesItem);
        }
      }).catch((error) => {
        if (error.code != resultStatus.TERMINATED) {
          output.error(output.DEVELOPERS_ERROR, "Can't build octree. " + error.message);
        }
      });

    } else {

      return new Promise((resolve) => {
        const data = buildOctreeData(indices, positions, this.bbox, this.isPointCloud);
        this._constructOctree(data, positionItem, indicesItem);
        resolve();
      }).catch((error) => {output.error(output.DEVELOPERS_ERROR, "Can't build octree. " + error)});

    }

  }

  buildNormalsAndBorder(useWorker, withNormals, withBorder) {
    const positionsItem = this.getLayoutItem("a_position");
    const indicesItem = this.getLayoutItem("indices");

    if (positionsItem.isDetached() || indicesItem.isDetached()) {
      return Promise.reject("Can't build normals. Some buffers are detached.");
    }

    const positions = positionsItem.getBuffer();
    const indices = indicesItem.getBuffer();

    let taskType;
    if (withNormals && withBorder) {
      taskType = messageType.BUILD_NORMALS_AND_BORDER
    } else if (withNormals) {
      taskType = messageType.BUILD_NORMALS;
    } else if (withBorder) {
      taskType = messageType.BUILD_BORDER;
    } else {
      return Promise.resolve();
    }

    if (useWorker) {

      positionsItem.setIsDetached(true);
      indicesItem.setIsDetached(true);

      const message = {
        indices,
        positions,
        type : taskType,
        tid : workerManager.generateUniqueID(),
        normalize : false
      };

      const transfer = [
        indices.buffer,
        positions.buffer,
      ];

      for (let i = 0; i < transfer.length; ++i) {
        memoryProfiler.markMemoryReleased(transfer[i]);
      }

      this._tid = message.tid;

      return workerManager.sendMessage(message, transfer, message.tid).then(({data}) => {

        if (data.status === resultStatus.ALL_OK) {
          indicesItem.setBuffer(data.indices.buffer);
          positionsItem.setBuffer(data.positions.buffer);

          if (data.normals) {
            const verticesNormalsItem = new GeometryBuffer({ buffer : data.normals.buffer, type : bufferType.F32_3 });
            this.setLayoutItem("a_normal", verticesNormalsItem);

            if (config.viewOnlyMode) {
              verticesNormalsItem.scheduleUnload();
            }
          }

          if (data.mask) {
            const borderMaskItem = new GeometryBuffer({buffer: data.mask.buffer, type: bufferType.UI8_1});
            this.setLayoutItem("vertices_border_mask", borderMaskItem);
          }

          if (config.viewOnlyMode) {
            indicesItem.scheduleUnload();
            positionsItem.scheduleUnload();
          }
        }
      }).catch((error) => {
        if (error.code !== resultStatus.TERMINATED) {
          output.error(output.DEVELOPERS_ERROR, "Can't build normals. " + error.message);
        }
      });

    } else {

      return new Promise((resolve) => {
        let verticesNormals = null;
        let verticesBorder = null;

        if (taskType === messageType.BUILD_NORMALS || taskType === messageType.BUILD_NORMALS_AND_BORDER) {
          verticesNormals = new Float32Array(positions.length);
        }

        if (taskType === messageType.BUILD_BORDER || taskType === messageType.BUILD_NORMALS_AND_BORDER) {
          verticesBorder = new Uint8Array(positions.length / 3);
        }

        buildNormals(indices, positions, verticesNormals);
        buildBordersMask(indices, verticesBorder);

        if (verticesNormals) {
          const verticesNormalsItem = new GeometryBuffer({ buffer : verticesNormals.buffer, type : bufferType.F32_3 });
          this.setLayoutItem("a_normal", verticesNormalsItem);

          if (config.viewOnlyMode) {
            verticesNormalsItem.scheduleUnload();
          }
        }

        if (verticesBorder) {
          const borderMaskItem = new GeometryBuffer({buffer: verticesBorder.buffer, type: bufferType.UI8_1});
          this.setLayoutItem("vertices_border_mask", borderMaskItem);
        }

        if (config.viewOnlyMode) {
          indicesItem.scheduleUnload();
          positionsItem.scheduleUnload();
        }
        resolve();
      }).catch((error) => {
        output.error(output.DEVELOPERS_ERROR, "Can't build normals. " + error);
      });

    }

  }

  invertNormals(useWorker) {

    const indicesItem = this.isPointCloud ? null : this.getLayoutItem("indices");
    const verticesNormalsItem = this.getLayoutItem("a_normal");

    if (indicesItem && indicesItem.isDetached() || verticesNormalsItem && verticesNormalsItem.isDetached()) {
      return Promise.reject("Can't invert normals. Geometry has detached buffers");
    }

    const indices = indicesItem ? indicesItem.getBuffer() : null;
    const verticesNormals = verticesNormalsItem ? verticesNormalsItem.getBuffer() : null;

    if (useWorker) {

      if (indicesItem) {
        indicesItem.setIsDetached(true);
      }
      if (verticesNormalsItem) {
        verticesNormalsItem.setIsDetached(true);
      }

      const message = {
        indices,
        verticesNormals,
        type : messageType.INVERT_NORMALS,
        tid : workerManager.generateUniqueID()
      };

      const transfer = [];
      if (indices) {
        transfer.push(indices.buffer);
      }
      if (verticesNormals) {
        transfer.push(verticesNormals.buffer);
      }

      for (const buffer of transfer) {
        memoryProfiler.markMemoryReleased(buffer);
      }

      this._tid = message.tid;

      return workerManager.sendMessage(message, transfer, message.tid).then(({data}) => {

        if (data.status === resultStatus.ALL_OK) {
          if (data.indices && indicesItem) {
            indicesItem.setBuffer(data.indices.buffer);
          }
          if (data.verticesNormals && verticesNormalsItem) {
            verticesNormalsItem.setBuffer(data.verticesNormals.buffer);
          }
        }
      }).catch((error) => {
        if (error.code !== resultStatus.TERMINATED) {
          output.error(output.DEVELOPERS_ERROR, "Can't invert normals. " + error.message);
        }
      });

    } else {

      return new Promise((resolve) => {
        invertVerticesNormals(verticesNormals, indices);
        resolve();
      }).catch((error) => {
        output.error(output.DEVELOPERS_ERROR, "Can't invert normals. " + error);
      });

    }

  }

  _constructOctree(data, positionItem, indicesItem) {

    indicesItem.setBuffer(data.indices.buffer);
    positionItem.setBuffer(data.positions.buffer);

    this.octree = new Octant(null);
    this.octree.buildFromBuffer(data.bboxBuffer, data.sizeBuffer, this.isPointCloud);
  }

  castRay(localOrigin, localDirection, dest) {

    const positions = this.getItemBuffer("a_position");

    if (!positions) {
      return -1;
    }

    const indices = this.getItemBuffer("indices");
    const sizes = this.getItemBuffer("a_size");
    let intersector;
    if (sizes) {
      intersector = (from, to, offset, length, result) => {
        return intersectEvaluator.sphereRayIntersection(from, to, indices, offset, length, positions, sizes, result);
      };
    } else {
      intersector = (from, to, offset, length, result) => {
        return intersectEvaluator.triangleRayIntersection(from, to, indices, offset, length, positions, result);
      };
    }

    if (this.octree) {
      return this.octree.castRay(localOrigin, localDirection, intersector, dest);
    } else {
      const length = indices ? indices.length : positions.length / 3;
      return this.bruteCastRay(localOrigin, localDirection, intersector, length, dest);
    }

  }

  getOctreeLeaves() {
    return this.octree.getLeaves();
  }

  checkHasMarkedVertices() {
    return this._hasMarkedVertices;
  }

  setHasMarkedVertices(has) {
    this._hasMarkedVertices = has;
  }

  checkHasSelectedVertices() {
    return this._hasSelectedVertices;
  }

  setHasSelectedVertices(has) {
    this._hasSelectedVertices = has;
    events.emit("geometry_selection_changed", {geometry: this, isSelected: has});
  }

  dropSelection() {

    const selectionMask = this.getItemBuffer("a_selection_mask");

    for (let i = 0; i < selectionMask.length; ++i) {
      selectionMask[i] = 0;
    }

    this.setHasSelectedVertices(false);

  }

  invertSelection() {

    const selectionMask = this.getItemBuffer("a_selection_mask");

    this.setHasSelectedVertices(false);
    for (let i = 0; i < selectionMask.length; ++i) {
      selectionMask[i] ^= 1;
      if (selectionMask[i]) {
        this.setHasSelectedVertices(true);
      }
    }
  }

  buildHalfEdge(useWorker) {

    const positionItem = this.getLayoutItem("a_position");
    const indicesItem = this.getLayoutItem("indices");

    if (positionItem.isDetached()) {
      return Promise.reject("Can't build Half Edges. Positions buffer is detached.");
    }

    const positions = positionItem.getBuffer();
    const indices = indicesItem.getBuffer();

    const verticesBorder = new Uint8Array(positions.length / 3);

    const numVertices = positions.length / 3;
    indicesItem.setIsDetached(true);

    if (useWorker) {

      const message = {
        indices : indices,
        vertMask : verticesBorder,
        numVertices : numVertices,
        type : messageType.BUILD_HALF_EDGES,
        tid : workerManager.generateUniqueID()
      };

      const transfer = [message.indices.buffer, message.vertMask.buffer];

      for (let i = 0; i < transfer.length; ++i) {
        memoryProfiler.markMemoryReleased(transfer[i]);
      }

      this._tid = message.tid;

      return workerManager.sendMessage(message, transfer, message.tid).then(({ data }) => {

        if (data.status === resultStatus.ALL_OK) {
          this._constructHalfEdges(data, indicesItem);
        }
      }).catch((error) => {
        if (error.code != resultStatus.TERMINATED) {
          output.error(output.DEVELOPERS_ERROR, "Can't build half edges. " + error.message);
        }
      });

    } else {

      return new Promise((resolve) => {
        const data = buildHalfEdgeData(indices, numVertices);
        data.vertMask = buildBordersMask(indices, verticesBorder);
        this._constructHalfEdges(data, indicesItem);
        resolve();
      }).catch((error) => {output.error(output.DEVELOPERS_ERROR, "Can't build half edges. " + error)});

    }

  }

  _constructHalfEdges(data, indicesItem) {

    const e2eType = arrayTools.uintArrayType(data.E2e);
    const edges2EdgesItem = new GeometryBuffer({ buffer : data.E2e.buffer, type : e2eType });
    this.setLayoutItem("edges2edges", edges2EdgesItem);

    const v2eType = arrayTools.uintArrayType(data.V2e);
    const verts2EdgesItem = new GeometryBuffer({ buffer : data.V2e.buffer, type : v2eType });
    this.setLayoutItem("verts2edges", verts2EdgesItem);

    const borderMaskItem = new GeometryBuffer({ buffer : data.mask.buffer, type : bufferType.I8_1 });
    this.setLayoutItem("border_mask", borderMaskItem);

    const vertexBorderMaskItem = new GeometryBuffer({buffer: data.vertMask.buffer, type: bufferType.UI8_1});
    this.setLayoutItem("vertices_border_mask", vertexBorderMaskItem);

    indicesItem.setBuffer(data.indices.buffer);

  }

  bruteCastRay(from, to, intersector, length, dest, meta = {}) {

    if (intersectEvaluator.AABBRayIntersect(from, to, this.bbox)) {

      const result = _INTERSECTION;

      const hit = intersector(from, to, 0, length, result);

      if (hit) {

        vec3.copy(dest, result.point);

        meta.primitive = result.primitive;

        return vec3.distance(result.point, from);

      }

    }

    return -1;

  }

  initVertexID(verticesCount) {
    const byteLength = verticesCount * arrayTools.getBytesPerElement(bufferType.F32_1);
    const vertexIDBuffer = new ArrayBuffer(byteLength);
    const vertexIDItem = new GeometryBuffer({ buffer : vertexIDBuffer, type : bufferType.F32_1 });

    this.setLayoutItem("a_vertex_id", vertexIDItem);

    const ids = this.getItemBuffer("a_vertex_id");
    for (let i = 0; i < ids.length; ++i) {
      ids[i] = i / ids.length;
    }

    arrayTools.shuffle(ids);
  }

  initOldIndices(vertRemoveMask) {
    const length = this.getVerticesCount();
    const typedArray = getTypedArrayByID(attributeId.OLD_INDICES);
    const array = new typedArray(length);

    let ptr = 0;
    for (let i=0; i<vertRemoveMask.length; i++) {
      if (!vertRemoveMask[i]) array[ptr++] = i;
    }

    const item = new GeometryBuffer({ buffer : array.buffer, type : getTypeByID(attributeId.OLD_INDICES) });
    this.setLayoutItem("old_indices", item);
  }

  initDistanceMap() {
    const length = this.getVerticesCount();
    const distances = new Float32Array(length);

    const distanceItem = new GeometryBuffer({buffer: distances.buffer, type: bufferType.F32_1});
    this.setLayoutItem("a_distance", distanceItem);
  }

  deleteVertexID() {
    delete this._layout["a_vertex_id"];
  }

  convertToInstancedPointCloud() {

    const numVertices = 4;

    const posByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
    const posBuffer = new ArrayBuffer(posByteLength);
    const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
    const indByteLength = numVertices * Uint8Array.BYTES_PER_ELEMENT;
    const vertIdBuffer = new ArrayBuffer(indByteLength);
    const vertexIdItem = new GeometryBuffer({ buffer : vertIdBuffer, type : bufferType.UI8_1 });
    const vertexIds = vertexIdItem.getBuffer();

    vertexIds.set([0.0, 1.0, 2.0, 3.0]);

    const indicesArray = arrayTools.createUintArrayByValueData(3, [0, 1, 2, 1, 3, 2]);
    const indicesType = arrayTools.uintArrayType(indicesArray, 3);
    const indicesItem = new GeometryBuffer({ buffer : indicesArray.buffer, type : indicesType });

    const globalPositionsItem = this.getLayoutItem("a_position");

    globalPositionsItem.divisor = 1;

    this.setLayoutItem("indices", indicesItem);
    this.setLayoutItem("a_position", positionsItem);
    this.setLayoutItem("a_vertex_id", vertexIdItem);
    this.setLayoutItem("a_global_position", globalPositionsItem);

  }

  setBbox(bbox) {
    this.bbox[0] = bbox[0];
    this.bbox[1] = bbox[1];
    this.bbox[2] = bbox[2];
    this.bbox[3] = bbox[3];
    this.bbox[4] = bbox[4];
    this.bbox[5] = bbox[5];
  }

  // gatherNeighbourVerts( startInd, includeBorder, storage) {

  //   const indices = this.getItemBuffer("indices");
  //   const E2e = this.getItemBuffer("edges2edges");
  //   const borderMask = this.getItemBuffer("border_mask");

  //   let numVerts = 0;

  //   if (borderMask[startInd] === -1) {

  //     if (includeBorder) {
  //       const vInd = indices[startInd];
  //       storage[numVerts++] = vInd;
  //     }

  //     startInd = startInd % 3 < 2 ? startInd + 1
  //                               : startInd - 2;
  //   }

  //   let currInd = startInd;

  //   // do not process more than 16 vertices
  //   while (numVerts < 16) {

  //     // opposite
  //     const oppInd = E2e[currInd];

  //     if (borderMask[oppInd] === -1) {

  //       if (includeBorder) {
  //         // next
  //         currInd = currInd % 3 < 2 ? currInd + 1
  //                                 : currInd - 2;

  //         const vInd = indices[currInd];
  //         storage[numVerts++] = vInd;

  //       }

  //       break;

  //     }

  //     const vInd = indices[oppInd];
  //     storage[numVerts++] = vInd;

  //     // next
  //     currInd = oppInd % 3 < 2 ? oppInd + 1
  //                            : oppInd - 2;

  //     if (currInd == startInd) {
  //       break;
  //     }

  //   }

  //   return numVerts;

  // }

  generateNewBuffers(vertCount, indCount) {

    for (let attrName of DEFAULT_ATTRIBUTES) {

      const item = this.getLayoutItem(attrName);

      if (!item) {
        continue;
      }

      if (attrName == "indices") {
        if (this.isPointCloud) {
          this._initPointCloudIndices();
        } else {
          const indicesArray = arrayTools.createUintArrayByValueLength(vertCount - 1, indCount);
          const stride = this.isPointCloud ? 1 : 3;
          const type = arrayTools.uintArrayType(indicesArray, stride);
          item.setBuffer(indicesArray.buffer, type);
        }
      } else {
        const byteLength = item.getComponentsCount() * vertCount * arrayTools.getBytesPerElement(item.type);
        item.setBuffer(new ArrayBuffer(byteLength));
      }

    }

    if (this.hasLayoutItem("a_selection_mask")) {
      this.initSelectionBuffers();
    }

    if (this.hasLayoutItem("a_vertex_id")) {
      this.initVertexID(vertCount);
    }

  }

  releaseMemory() {
    for (let bufferName in this._layout) {
      const geometryBuffer = this._layout[bufferName];
      memoryProfiler.markMemoryReleased(geometryBuffer.buffer);
    }
  }

  terminateDataBuilding() {
    workerManager.terminateByID(this._tid);
  }

  recalculateBoundings() {

    const indices = this.getItemBuffer("indices");
    const positions = this.getItemBuffer("a_position");

    this.octree.recalculateBoundings(indices, positions);

    minmaxUtils.copy(this.bbox, this.octree._AABB);

  }

  empty() {

    const positionItem = this.getLayoutItem("a_position");

    if (positionItem.isDetached()) {
      return false;
    }

    const positions = this.getItemBuffer("a_position");

    return (!positions || positions.length === 0);
  }

  _initPointCloudIndices() {

    const positions = this.getItemBuffer("a_position");

    if (!positions) {
      return;
    }

    const indicesData = [];
    const maxValue = positions.length / 3 - 1;
    for (let i = 0; i <= maxValue; ++i) {
      indicesData.push(i);
    }

    const indices = arrayTools.createUintArrayByValueData(maxValue, indicesData);
    const indicesType = arrayTools.uintArrayType(indices, 1);
    const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

    this.setLayoutItem("indices", indicesItem);

  }

  deletePointCloudIndices() {
    delete this._layout["indices"];
  }

  deleteVerticesBorderMask() {
    delete this._layout["vertices_border_mask"];
  }

}

class GeometryBuffer {

  constructor({ buffer, type, byteOffset = 0, stride = 0, divisor = 0 }) {

    if (!(buffer instanceof ArrayBuffer)) {
      output.error(output.DEVELOPERS_ERROR, "Can't create geometry buffer. Wrong buffer type! ArrayBuffer expected.");
    }

    this.buffer = buffer;
    this.type = type;
    this.byteOffset = byteOffset;
    this.stride = stride;
    this.divisor = divisor;
    this._isDetached = false;
    this._shouldBeUnloaded = false;

    memoryProfiler.markMemoryAllocated(this.buffer, memoryType.CPU_GEOMETRY, this.buffer.byteLength);

  }

  scheduleUnload() {
    this._shouldBeUnloaded = true;
  }

  shouldBeUnloaded() {
    return this._shouldBeUnloaded;
  }

  getBuffer() {
    if (!this._isDetached) {
      return arrayTools.castBuffer(this.buffer, this.type);
    } else {
      return null;
    }
  }

  castBuffer(type) {
    return arrayTools.castBuffer(this.buffer, type);
  }

  setBuffer(buffer, type = this.type) {

    if (!(buffer instanceof ArrayBuffer)) {
      output.error(output.DEVELOPERS_ERROR, "Can't set geometry buffer. Wrong buffer type! ArrayBuffer expected.");
      return;
    }

    if (this.buffer) {
      memoryProfiler.markMemoryReleased(this.buffer);
    }

    memoryProfiler.markMemoryAllocated(buffer, memoryType.CPU_GEOMETRY, buffer.byteLength);
    this.buffer = buffer;
    this.type = type;
    this.setIsDetached(false);
  }

  getLength() {

    const byteLength = this.buffer.byteLength;

    let byteSize = 4;

    switch (this.type) {
      case bufferType.F32_1:
      case bufferType.F32_2:
      case bufferType.F32_3:
      case bufferType.F32_4:
        byteSize = Float32Array.BYTES_PER_ELEMENT;
        break;
      case bufferType.UI8_1:
      case bufferType.UI8_2:
      case bufferType.UI8_3:
      case bufferType.UI8_4:
        byteSize = Uint8Array.BYTES_PER_ELEMENT;
        break;
    }

    return byteLength / byteSize;

  }

  getComponentsCount() {

    switch (this.type) {
      case bufferType.F32_1:
      case bufferType.UI8_1:
      case bufferType.UI16_1:
      case bufferType.UI32_1:
        return 1;
      case bufferType.F32_2:
      case bufferType.UI8_2:
      case bufferType.UI16_2:
      case bufferType.UI32_2:
        return 2;
      case bufferType.F32_3:
      case bufferType.UI8_3:
      case bufferType.UI16_3:
      case bufferType.UI32_3:
        return 3;
      case bufferType.F32_4:
      case bufferType.UI8_4:
      case bufferType.UI16_4:
      case bufferType.UI32_4:
        return 4;
    }

  }

  getByteOffset() {
    return this.byteOffset;
  }

  getStride() {
    return this.stride;
  }

  getDivisor() {
    return this.divisor;
  }

  isDetached() {
    return this._isDetached;
  }

  setIsDetached(isDetached) {
    this._isDetached = isDetached;
  }

}

export { Geometry, GeometryBuffer, DEPENDENT_BUFFER_NAMES }
